import React, { useState, useEffect } from 'react';
import PageHeader from '../../../components/PageHeader';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { evolutionService } from '../services/evolutionService';

interface MonthData { month: string; total: number; }
interface ClassificationData { name: string; months: MonthData[]; }
interface GroupData { group: string; classifications: ClassificationData[]; }

const formatCurrency = (v: number) => {
  if (isNaN(v)) console.warn('[Evolution] NaN detected in formatCurrency', new Error().stack?.split('\n')[2]);
  return Math.abs(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};
const formatMonth = (m: string) => { const [y, mo] = m.split('-'); const names = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez']; return `${names[+mo - 1]}/${y.slice(2)}`; };
const currentMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };

function getAmountColor(value: number): string {
  if (value === 0) return 'text-gray-300';
  return value > 0 ? 'text-green-700' : 'text-red-700';
}

function getAmountColorLight(value: number): string {
  if (value === 0) return 'text-gray-300';
  return value > 0 ? 'text-green-600' : 'text-red-600';
}

const GroupRows: React.FC<{ grp: GroupData; visibleMonthIndices: number[]; visibleMonths: string[]; budgetMap: Record<string, number>; showTotals: boolean }> = ({ grp, visibleMonthIndices, visibleMonths, budgetMap, showTotals }) => {
  const hasVisibleClassification = grp.classifications.some(sub => {
    const total = visibleMonthIndices.reduce((s, i) => s + (sub.months[i]?.total || 0), 0);
    const hasBudget = visibleMonthIndices.some(i => budgetMap[`${grp.group}||${sub.name}||${sub.months[i]?.month}`]);
    return total !== 0 || hasBudget;
  });
  if (!hasVisibleClassification) return null;
  const cityTotals = visibleMonthIndices.map(i => grp.classifications.reduce((s, sub) => s + (sub.months[i]?.total || 0), 0));
  const cityTotal = cityTotals.reduce((s, v) => s + v, 0);
  return (
    <React.Fragment>
      <tr className="bg-blue-50 border-b">
        <td className="px-3 py-2 font-semibold text-blue-800 sticky left-0 bg-blue-50">{grp.group}</td>
        {cityTotals.map((v, vi) => {
          const explicitBudget = budgetMap[`${grp.group}||||${visibleMonths[vi]}`] || 0;
          const fallbackFromNoBudget = grp.classifications.reduce((s, sub) => {
            const subBudget = budgetMap[`${grp.group}||${sub.name}||${sub.months[visibleMonthIndices[vi]]?.month}`] || 0;
            if (subBudget) return s; // has its own budget, skip
            const spent = Math.abs(sub.months[visibleMonthIndices[vi]]?.total || 0);
            return s + spent;
          }, 0);
          const budget = explicitBudget + fallbackFromNoBudget;
          const diff = budget + v;
          return (
            <td key={visibleMonths[vi]} className={`px-3 py-2 text-right font-semibold ${getAmountColor(v)}`}>
              {v === 0 ? '-' : formatCurrency(v)}{budget ? <span className="text-gray-400 font-normal"> / {formatCurrency(budget)}</span> : ''}{budget ? <span className={`text-xs font-normal ${diff >= 0 ? 'text-green-600' : 'text-orange-600'}`}> ({diff >= 0 ? '+' : ''}{formatCurrency(diff)})</span> : ''}
            </td>
          );
        })}
        {showTotals && (() => {
          const budgetTotal = visibleMonths.reduce((s, m, vi) => {
            const explicit = budgetMap[`${grp.group}||||${m}`] || 0;
            const fallback = grp.classifications.reduce((cs, sub) => {
              const subBudget = budgetMap[`${grp.group}||${sub.name}||${sub.months[visibleMonthIndices[vi]]?.month}`] || 0;
              if (subBudget) return cs;
              return cs + Math.abs(sub.months[visibleMonthIndices[vi]]?.total || 0);
            }, 0);
            return s + explicit + fallback;
          }, 0);
          const diff = budgetTotal + cityTotal;
          return <>
            <td className={`px-3 py-2 text-right font-bold bg-blue-100 ${cityTotal >= 0 ? 'text-green-800' : 'text-red-800'}`}>{formatCurrency(cityTotal)}{budgetTotal ? <span className={`font-normal text-xs ${diff >= 0 ? 'text-green-600' : 'text-orange-600'}`}> ({diff >= 0 ? '+' : ''}{formatCurrency(diff)})</span> : ''}</td>
            <td className={`px-3 py-2 text-right font-bold bg-blue-100 ${cityTotal >= 0 ? 'text-green-800' : 'text-red-800'}`}>{formatCurrency(cityTotal / (cityTotals.filter((v, vi) => v !== 0 && visibleMonths[vi] !== currentMonth()).length || 1))}</td>
          </>;
        })()}
      </tr>
      {grp.classifications.map(sub => {
        const total = visibleMonthIndices.reduce((s, i) => s + (sub.months[i]?.total || 0), 0);
        const hasBudget = visibleMonthIndices.some(i => budgetMap[`${grp.group}||${sub.name}||${sub.months[i]?.month}`]);
        if (total === 0 && !hasBudget) return null;
        return (
          <tr key={sub.name} className="border-b hover:bg-gray-50">
            <td className="px-1 py-1.5 pl-3 sm:px-3 sm:pl-6 text-gray-700 sticky left-0 bg-white whitespace-nowrap">{sub.name}</td>
            {visibleMonthIndices.map(i => {
              const monthData = sub.months[i] || { month: '', total: 0 };
              const classBudget = budgetMap[`${grp.group}||${sub.name}||${monthData.month}`] || 0;
              const effectiveBudget = classBudget || (monthData.total !== 0 ? Math.abs(monthData.total) : 0);
              const isFallback = !classBudget && monthData.total !== 0;
              return (
                <td key={monthData.month || i} className={`px-3 py-1.5 text-right whitespace-nowrap ${getAmountColorLight(monthData.total)}`}>
                  {monthData.total === 0 ? '-' : formatCurrency(monthData.total)}{effectiveBudget ? <span className={isFallback ? 'text-orange-500' : 'text-gray-400'}> / {formatCurrency(effectiveBudget)}</span> : ''}
                </td>
              );
            })}
            {showTotals && <td className={`px-3 py-1.5 text-right font-semibold bg-gray-50 ${total >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(total)}</td>}
            {showTotals && <td className={`px-3 py-1.5 text-right text-gray-600 bg-gray-50`}>{formatCurrency(total / (visibleMonthIndices.filter(i => (sub.months[i]?.total || 0) !== 0 && sub.months[i]?.month !== currentMonth()).length || 1))}</td>}
          </tr>
        );
      })}
    </React.Fragment>
  );
};

function parseGroups(rawGroups: any[]): GroupData[] {
  return (rawGroups || []).map((g: any) => ({
    group: g.group || '',
    classifications: (g.classifications || []).map((c: any) => ({
      ...c,
      months: (c.months || []).map((m: any) => ({ month: m.month, total: Number(m.total) || 0 })),
    })),
  }));
}

const EvolutionPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [allMonths, setAllMonths] = useState<string[]>([]);
  const [selectedMonths, setSelectedMonths] = useState<string[]>(() => {
    try { const s = localStorage.getItem('fin_evolution_months'); if (s) return JSON.parse(s); } catch {}
    const now = new Date(); return [`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`];
  });
  const [groups, setGroups] = useState<GroupData[]>([]);
  const [budgetMap, setBudgetMap] = useState<Record<string, number>>({});

  const loadEvolutionData = async () => {
    const res = await evolutionService.getEvolution();
    const d = res.data.data;
    const months: string[] = d.allMonths || [];
    setAllMonths(months);
    const monthSet = new Set(months);
    setSelectedMonths(prev => prev.length ? prev.filter(monthSet.has.bind(monthSet)) : months);
    setGroups(parseGroups(d.groups));
    console.log('[Evolution] groups sample:', JSON.stringify((d.groups || [])[0]?.classifications?.[0]?.months?.slice(0, 2)));
    if (!months.length) return;

    // Budgets come in the same response with resolved names
    const bMap: Record<string, number> = {};
    if (d.budgets) {
      // Build group ID→name and classifier ID→name maps
      const groupMap: Record<string, string> = {};
      for (const a of (d.agrupadores || []) as { _id: string; name: string }[]) groupMap[a._id] = a.name;
      const classifierMap: Record<string, string> = {};
      for (const c of (d.classificadores || []) as { _id: string; name: string }[]) classifierMap[c._id] = c.name;

      for (const b of d.budgets as { groupId: string; classifierId: string; month: string; amount: number }[]) {
        const groupName = groupMap[b.groupId] || '';
        const classifierName = classifierMap[b.classifierId] || '';
        bMap[`${groupName}||${classifierName}||${b.month}`] = Number(b.amount) || 0;
      }
    }
    setBudgetMap(bMap);
  };

  useEffect(() => { loadEvolutionData().finally(() => setLoading(false)); }, []); // eslint-disable-line

  if (loading) return <LoadingSpinner />;

  if (!groups.length) return (
    <><PageHeader title="Evolução por Classificação" /><div className="p-8 text-center text-gray-500">Nenhuma transação com classificação definida. Classifique transações na tela inicial.</div></>
  );

  // Filter months based on selection
  const visibleMonthIndices = allMonths.map((m, i) => selectedMonths.includes(m) ? i : -1).filter(i => i >= 0);
  const visibleMonths = visibleMonthIndices.map(i => allMonths[i]);

  const grandTotal = groups.reduce((s, c) => s + c.classifications.reduce((ss, sub) => ss + visibleMonthIndices.reduce((a, i) => a + (sub.months[i]?.total || 0), 0), 0), 0);
  const activeMonthsCount = visibleMonths.filter((m, vi) => m !== currentMonth() && groups.some(c => c.classifications.some(sub => (sub.months[visibleMonthIndices[vi]]?.total || 0) !== 0))).length || 1;
  const grandTotalColor = grandTotal >= 0 ? 'text-green-800' : 'text-red-800';
  const showTotals = visibleMonths.length > 1;

  const toggleMonth = (m: string) => setSelectedMonths(prev => { const next = prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]; localStorage.setItem('fin_evolution_months', JSON.stringify(next)); return next; });
  const selectAllMonths = () => { setSelectedMonths(allMonths); localStorage.setItem('fin_evolution_months', JSON.stringify(allMonths)); };
  const clearMonths = () => { setSelectedMonths([]); localStorage.setItem('fin_evolution_months', JSON.stringify([])); };

  const getGroupEffectiveBudget = (grp: GroupData, mi: number, month: string) => {
    const explicit = budgetMap[`${grp.group}||||${month}`] || 0;
    const fallback = grp.classifications.reduce((cs, sub) => {
      const subBudget = budgetMap[`${grp.group}||${sub.name}||${sub.months[mi]?.month}`] || 0;
      if (subBudget) return cs;
      return cs + Math.abs(sub.months[mi]?.total || 0);
    }, 0);
    return explicit + fallback;
  };

  const totalBudgetAll = visibleMonthIndices.reduce((s, mi, vi) => s + groups.reduce((gs, c) => gs + getGroupEffectiveBudget(c, mi, visibleMonths[vi]), 0), 0);
  const totalBudgetDiff = totalBudgetAll + grandTotal;

  return (
    <>
      <PageHeader title="Evolução por Classificação" />
      {/* Month selector */}
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-medium text-gray-600">Competências:</span>
          <button onClick={selectAllMonths} className="text-xs text-blue-600 hover:underline">Todas</button>
          <button onClick={clearMonths} className="text-xs text-blue-600 hover:underline">Nenhuma</button>
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-6 md:flex md:flex-wrap gap-1">
          {allMonths.map(m => (
            <button key={m} onClick={() => toggleMonth(m)}
              className={`text-xs px-2 py-1.5 sm:py-0.5 rounded border text-center ${selectedMonths.includes(m) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}
            >{formatMonth(m)}</button>
          ))}
        </div>
      </div>
      <div className="px-1 py-4 sm:px-4 overflow-x-auto">
        <table className="w-full text-sm border rounded-lg">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600 sticky left-0 bg-gray-50 z-10">Agrupador / Classificação</th>
              {visibleMonths.map(m => (
                <th key={m} className="px-3 py-2 text-right font-medium text-gray-600 whitespace-nowrap">{formatMonth(m)}</th>
              ))}
              {showTotals && <th className="px-3 py-2 text-right font-medium text-gray-900 bg-gray-100">Total</th>}
              {showTotals && <th className="px-3 py-2 text-right font-medium text-gray-900 bg-gray-100">Média/Mês</th>}
            </tr>
          </thead>
          <tbody>
            {groups.map(grp => <GroupRows key={grp.group} grp={grp} visibleMonthIndices={visibleMonthIndices} visibleMonths={visibleMonths} budgetMap={budgetMap} showTotals={showTotals} />)}
          </tbody>
          <tfoot className="bg-gray-100 border-t">
            <tr>
              <td className="px-3 py-2 font-semibold text-gray-900 sticky left-0 bg-gray-100">Total Geral</td>
              {visibleMonthIndices.map((mi, vi) => {
                const monthTotal = groups.reduce((s, c) => s + c.classifications.reduce((ss, sub) => ss + (sub.months[mi]?.total || 0), 0), 0);
                const monthBudget = groups.reduce((s, c) => s + getGroupEffectiveBudget(c, mi, visibleMonths[vi]), 0);
                const diff = monthBudget + monthTotal;
                return <td key={visibleMonths[vi]} className="px-3 py-2 text-right font-semibold">
                  <span className={monthTotal >= 0 ? 'text-green-700' : 'text-red-700'}>{formatCurrency(monthTotal)}</span>
                  {monthBudget ? <span className="text-orange-500 font-normal"> / {formatCurrency(monthBudget)}</span> : ''}
                  {monthBudget ? <span className={`text-xs font-normal ${diff >= 0 ? 'text-green-600' : 'text-orange-600'}`}> ({diff >= 0 ? '+' : ''}{formatCurrency(diff)})</span> : ''}
                </td>;
              })}
              {showTotals && <>
                <td className={`px-3 py-2 text-right font-bold ${grandTotalColor}`}>{formatCurrency(grandTotal)}{totalBudgetAll ? <span className={`font-normal text-xs ${totalBudgetDiff >= 0 ? 'text-green-600' : 'text-orange-600'}`}> ({totalBudgetDiff >= 0 ? '+' : ''}{formatCurrency(totalBudgetDiff)})</span> : ''}</td>
                <td className={`px-3 py-2 text-right font-bold ${grandTotalColor}`}>{formatCurrency(grandTotal / activeMonthsCount)}</td>
              </>}
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
};

export default EvolutionPage;
